// src/annihilation/SunkenFold3D.jsx
// ============================================================
//  ANNIHILATION — THE SUNKEN FOLD, IN 3D   (Batch 3D-ENV-1)
//  The first dungeon in 3D. Two chambers, one renderer, switched
//  by `stage` ("descent" | "crypt"). PRESENTATIONAL only — the
//  Fold (sunkenFold.js) keeps owning the prowler phase clock, the
//  Matriarch's phases, the familiarity teach, the cubs, all of it.
//  This file DRAWS the live state it's handed and nothing more.
//
//  3D-ENV-1 — THE GORGEOUS WORLD PASS. Painted stone-block walls
//  (mortar courses, damp streaks, moss at the floor line) with a
//  carved rim course, rune-banded pillars that glow cold, seeded
//  crypt decor derived from geo.bounds (rubble, old bones, corner
//  cobwebs, hanging roots in the Descent), a creeping floor mist,
//  and slow cold dust motes. ZERO new lights (all glow emissive +
//  toneMapped false), ZERO new shadow casters, all decor static +
//  memo'd + seeded. Same props, same chamber switch, same swap.
//
//  What 3D genuinely buys here:
//   · The four crypt pillars are REAL geometry — line-of-sight is
//     literal now, not simulated. When the spec says "a pillar
//     breaks the Roar," in 3D you can SEE the pillar between you
//     and her.
//   · Phasing prowlers go translucent + emissive when phased
//     (Spectral family law), unmistakable.
//   · The Crypt uses the STAGE camera (a fixed cinematic mark that
//     only re-aims at the actor) — the enclosed-room archetype
//     with zero camera bugs. The Descent corridor uses follow.
//
//  Same props as the 2D chambers → one-line swap, flag-gated.
// ============================================================

import { useMemo, useRef, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import {
  PlayerBody, EnemyMesh, Floaty, Label, GroundBlob, FollowRig, StageRig, TouchControls,
  mulberry32, hashId, canvasTex, familyColor,
} from "./fold3dKit";
import { levelForXp, tierForLevel } from "./stats";

const UNIT = 2.2;   // tight — it's a crypt

function makeWorld(bounds) {
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cy = (bounds.minY + bounds.maxY) / 2;
  return (gx, gy) => [(gx - cx) * UNIT, 0, (gy - cy) * UNIT];
}

// ────────────────────────────────────────────────────────────
// PAINTED TEXTURES (3D-ENV-1) — cached per key, seeded.
// ────────────────────────────────────────────────────────────

// ── flagstone floor, damp and cold — richer now: irregular slab
// grid with mortar shadow, hairline cracks, bone dust, damp sheen.
function floorTex(key, tint) {
  return canvasTex(`${key}_env1`, 256, 256, (g, w, h) => {
    g.fillStyle = tint; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(key) + 5);
    // irregular flagstones
    const N = 5;
    for (let ry = 0; ry < N; ry++) {
      for (let cx = 0; cx < N; cx++) {
        const x = (cx / N) * w + (R() - 0.5) * 5, y = (ry / N) * h + (R() - 0.5) * 5;
        const sw = w / N - 4, sh = h / N - 4;
        g.fillStyle = `rgba(${R() > 0.5 ? "255,255,255" : "0,0,0"},${0.02 + R() * 0.05})`;
        g.fillRect(x + 2, y + 2, sw, sh);
        g.strokeStyle = "rgba(0,0,0,0.55)"; g.lineWidth = 2.5;
        g.strokeRect(x + 2, y + 2, sw, sh);
      }
    }
    // grit + bone dust
    for (let i = 0; i < 600; i++) {
      const pale = R() > 0.8;
      g.fillStyle = pale ? "rgba(180,175,160,0.18)" : (R() > 0.5 ? "rgba(0,0,0,0.3)" : "rgba(70,80,90,0.15)");
      g.fillRect(R() * w, R() * h, pale ? 2 : 3, pale ? 2 : 3);
    }
    // hairline cracks wandering across slabs
    g.strokeStyle = "rgba(0,0,0,0.45)"; g.lineWidth = 1.2;
    for (let i = 0; i < 7; i++) {
      let x = R() * w, y = R() * h;
      g.beginPath(); g.moveTo(x, y);
      for (let s = 0; s < 5; s++) { x += (R() - 0.5) * 30; y += (R() - 0.5) * 30; g.lineTo(x, y); }
      g.stroke();
    }
    // damp sheen patches
    for (let i = 0; i < 10; i++) {
      const x = R() * w, y = R() * h, r = 18 + R() * 32;
      const m = g.createRadialGradient(x, y, 2, x, y, r);
      m.addColorStop(0, "rgba(50,74,86,0.22)");
      m.addColorStop(1, "rgba(50,74,86,0)");
      g.fillStyle = m; g.beginPath(); g.ellipse(x, y, r, r * 0.7, R() * 3, 0, 7); g.fill();
    }
  });
}

// ── stone-block wall — mortar courses, tone-varied blocks, damp
// streaks running down from the ceiling, moss at the floor line.
function wallTex() {
  return canvasTex("fold_wall_env1", 256, 256, (g, w, h) => {
    g.fillStyle = "#26282c"; g.fillRect(0, 0, w, h);
    const R = mulberry32(87);
    const BH = 26;
    for (let ry = 0; ry < h / BH + 1; ry++) {
      const ox = (ry % 2) * 30;
      for (let x = -60; x < w; x += 60) {
        const tone = 40 + R() * 18;
        g.fillStyle = `rgb(${tone},${tone + 2},${tone + 6})`;
        g.fillRect(x + ox + 2, ry * BH + 2, 56, BH - 4);
        g.strokeStyle = "rgba(0,0,0,0.6)"; g.lineWidth = 2.5;
        g.strokeRect(x + ox + 2, ry * BH + 2, 56, BH - 4);
        // chipped corner highlights
        if (R() > 0.6) {
          g.fillStyle = "rgba(255,255,255,0.05)";
          g.fillRect(x + ox + 4, ry * BH + 4, 14, 3);
        }
      }
    }
    // damp streaks running down
    for (let i = 0; i < 14; i++) {
      const x = R() * w, l = 40 + R() * 120;
      const st = g.createLinearGradient(0, 0, 0, l);
      st.addColorStop(0, "rgba(30,44,52,0.35)");
      st.addColorStop(1, "rgba(30,44,52,0)");
      g.save(); g.translate(x, R() * h * 0.3);
      g.fillStyle = st; g.fillRect(-2, 0, 4 + R() * 3, l);
      g.restore();
    }
    // moss at the floor line
    for (let i = 0; i < 50; i++) {
      const y = h - Math.pow(R(), 2) * h * 0.25;
      g.fillStyle = `rgba(56,74,44,${0.2 + R() * 0.3})`;
      g.beginPath(); g.arc(R() * w, y, 2 + R() * 5, 0, 7); g.fill();
    }
  });
}

// ── pillar stone — vertical fluting + a carved rune band. The
// texture feeds map AND emissiveMap: only the runes are bright,
// so the band glows cold with zero lights.
function pillarTex() {
  return canvasTex("fold_pillar_env1", 128, 256, (g, w, h) => {
    g.fillStyle = "#33353a"; g.fillRect(0, 0, w, h);
    const R = mulberry32(139);
    // vertical fluting shadows
    for (let i = 0; i < 8; i++) {
      const x = (i / 8) * w;
      const fg = g.createLinearGradient(x, 0, x + w / 8, 0);
      fg.addColorStop(0, "rgba(0,0,0,0.3)");
      fg.addColorStop(0.5, "rgba(255,255,255,0.05)");
      fg.addColorStop(1, "rgba(0,0,0,0.3)");
      g.fillStyle = fg; g.fillRect(x, 0, w / 8, h);
    }
    // weathering
    for (let i = 0; i < 300; i++) {
      g.fillStyle = `rgba(0,0,0,${0.05 + R() * 0.1})`;
      g.fillRect(R() * w, R() * h, 2 + R() * 3, 2 + R() * 3);
    }
    // the carved rune band — mid-height, spectral teal
    const bandY = h * 0.42, bandH = 26;
    g.fillStyle = "rgba(0,0,0,0.4)"; g.fillRect(0, bandY - 4, w, bandH + 8);
    g.strokeStyle = "rgba(140,220,230,0.9)"; g.lineWidth = 2;
    for (let i = 0; i < 7; i++) {
      const x = 8 + i * 17, y = bandY + 4;
      g.beginPath();
      g.moveTo(x, y); g.lineTo(x + 8, y + 16);
      g.moveTo(x + 8, y); g.lineTo(x, y + 16);
      if (R() > 0.5) { g.moveTo(x + 4, y - 2); g.lineTo(x + 4, y + 18); }
      g.stroke();
    }
  });
}

function CryptFloor({ bounds, tint }) {
  const tex = useMemo(() => floorTex(`fold_floor_${tint}`, tint), [tint]);
  useMemo(() => { tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(4, 3); }, [tex]);
  const w = (bounds.maxX - bounds.minX + 2) * UNIT;
  const d = (bounds.maxY - bounds.minY + 2) * UNIT;
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <planeGeometry args={[w, d]} />
      <meshStandardMaterial map={tex} roughness={1} />
    </mesh>
  );
}

// stone walls around the chamber — painted block texture now,
// with a carved rim course running the top of every wall.
function Walls({ bounds }) {
  const tex = useMemo(wallTex, []);
  useMemo(() => { tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(4, 1); }, [tex]);
  const w = (bounds.maxX - bounds.minX + 2) * UNIT;
  const d = (bounds.maxY - bounds.minY + 2) * UNIT;
  const H = 4.5;
  const seg = (x, z, sw, sd, k) => (
    <group key={k}>
      <mesh position={[x, H / 2, z]} castShadow receiveShadow>
        <boxGeometry args={[sw, H, sd]} />
        <meshStandardMaterial map={tex} roughness={1} />
      </mesh>
      {/* carved rim course along the top */}
      <mesh position={[x, H + 0.12, z]}>
        <boxGeometry args={[sw + 0.16, 0.24, sd + 0.16]} />
        <meshStandardMaterial color="#1c1e22" roughness={1} />
      </mesh>
    </group>
  );
  return (
    <group>
      {seg(0, -d / 2, w, 0.6, "n")}
      {seg(0, d / 2, w, 0.6, "s")}
      {seg(-w / 2, 0, 0.6, d, "w")}
      {seg(w / 2, 0, 0.6, d, "e")}
    </group>
  );
}

// ── CRYPT DECOR (3D-ENV-1) — the room remembers what happened
// here. Rubble heaps, old bones, cobwebs stretched in the wall
// corners, and (Descent only) roots hanging from the unseen
// ceiling. Seeded from the bounds; static; memo-safe; casts no
// shadows; collides with nothing — pure presentation.
function CryptDecor({ bounds, roots = false }) {
  const items = useMemo(() => {
    const R = mulberry32(719 + (roots ? 13 : 0));
    const w = (bounds.maxX - bounds.minX + 2) * UNIT;
    const d = (bounds.maxY - bounds.minY + 2) * UNIT;
    const out = [];
    // rubble along the wall lines (keeps the fighting floor clear)
    for (let i = 0; i < 9; i++) {
      const side = i % 4;
      const t = R() - 0.5;
      let x, z;
      if (side === 0) { x = t * w * 0.86; z = -d / 2 + 0.9 + R() * 0.5; }
      else if (side === 1) { x = t * w * 0.86; z = d / 2 - 0.9 - R() * 0.5; }
      else if (side === 2) { x = -w / 2 + 0.9 + R() * 0.5; z = t * d * 0.86; }
      else { x = w / 2 - 0.9 - R() * 0.5; z = t * d * 0.86; }
      out.push({ t: "rubble", x, z, s: 0.5 + R() * 0.7, a: R() * 6, k: `rb${i}` });
    }
    // bones — small pale scatters, also hugging the walls
    for (let i = 0; i < 6; i++) {
      const side = i % 4;
      const t = R() - 0.5;
      let x, z;
      if (side === 0) { x = t * w * 0.8; z = -d / 2 + 1.4 + R() * 0.8; }
      else if (side === 1) { x = t * w * 0.8; z = d / 2 - 1.4 - R() * 0.8; }
      else if (side === 2) { x = -w / 2 + 1.4 + R() * 0.8; z = t * d * 0.8; }
      else { x = w / 2 - 1.4 - R() * 0.8; z = t * d * 0.8; }
      out.push({ t: "bones", x, z, s: 0.5 + R() * 0.5, a: R() * 6, k: `bn${i}` });
    }
    // cobwebs in the four wall corners
    const cw = w / 2 - 0.5, cd = d / 2 - 0.5;
    [[-cw, -cd, 0.8], [cw, -cd, -0.8], [-cw, cd, 2.4], [cw, cd, -2.4]].forEach(([x, z, a], i) => {
      out.push({ t: "web", x, z, a, s: 0.8 + R() * 0.5, k: `wb${i}` });
    });
    // hanging roots (Descent only) — down the corridor edges
    if (roots) {
      for (let i = 0; i < 10; i++) {
        const t = R() - 0.5;
        const side = R() > 0.5 ? 1 : -1;
        out.push({
          t: "root", x: t * w * 0.8, z: side * (d / 2 - 1.1 - R() * 0.6),
          s: 0.6 + R() * 0.9, a: R() * 6, sway: R() * 6, k: `rt${i}`,
        });
      }
    }
    return out;
  }, [bounds, roots]);
  return (
    <group>
      {items.map((it) => {
        if (it.t === "rubble") {
          return (
            <group key={it.k} position={[it.x, 0, it.z]} rotation={[0, it.a, 0]}>
              <mesh position={[0, 0.14 * it.s, 0]} scale={[1, 0.6, 0.85]}>
                <dodecahedronGeometry args={[0.3 * it.s, 0]} />
                <meshStandardMaterial color="#2c2e33" roughness={1} />
              </mesh>
              <mesh position={[0.26 * it.s, 0.08 * it.s, 0.12]} rotation={[0.3, 0.5, 0.2]}>
                <boxGeometry args={[0.24 * it.s, 0.2 * it.s, 0.24 * it.s]} />
                <meshStandardMaterial color="#33353a" roughness={1} />
              </mesh>
              <mesh position={[-0.2 * it.s, 0.05 * it.s, -0.1]}>
                <dodecahedronGeometry args={[0.12 * it.s, 0]} />
                <meshStandardMaterial color="#26282c" roughness={1} />
              </mesh>
            </group>
          );
        }
        if (it.t === "bones") {
          return (
            <group key={it.k} position={[it.x, 0, it.z]} rotation={[0, it.a, 0]}>
              {/* a long bone + a rib curve + a small skull-ish knob */}
              <mesh position={[0, 0.045, 0]} rotation={[0, 0.3, Math.PI / 2]}>
                <cylinderGeometry args={[0.03 * it.s, 0.035 * it.s, 0.5 * it.s, 5]} />
                <meshStandardMaterial color="#b8b0a0" roughness={1} />
              </mesh>
              <mesh position={[0.16 * it.s, 0.05, 0.12]} rotation={[Math.PI / 2, 0, 0.8]}>
                <torusGeometry args={[0.1 * it.s, 0.02 * it.s, 5, 8, Math.PI]} />
                <meshStandardMaterial color="#aca496" roughness={1} />
              </mesh>
              <mesh position={[-0.18 * it.s, 0.07 * it.s, -0.06]} scale={[1, 0.85, 1.1]}>
                <sphereGeometry args={[0.08 * it.s, 7, 6]} />
                <meshStandardMaterial color="#b0a898" roughness={1} />
              </mesh>
            </group>
          );
        }
        if (it.t === "web") {
          return (
            <mesh key={it.k} position={[it.x, 3.6, it.z]} rotation={[0.5, it.a, 0]}>
              <circleGeometry args={[0.7 * it.s, 3]} />
              <meshBasicMaterial color="#c8ccd4" transparent opacity={0.1} side={THREE.DoubleSide} depthWrite={false} />
            </mesh>
          );
        }
        // root — a thin tapered chain hanging from above
        return (
          <group key={it.k} position={[it.x, 4.4, it.z]} rotation={[0.08 * Math.sin(it.sway), it.a, 0.08 * Math.cos(it.sway)]}>
            <mesh position={[0, -0.7 * it.s, 0]}>
              <cylinderGeometry args={[0.02, 0.05, 1.4 * it.s, 5]} />
              <meshStandardMaterial color="#2c2418" roughness={1} />
            </mesh>
            <mesh position={[0.06, -1.2 * it.s, 0.03]} rotation={[0, 0, 0.4]}>
              <cylinderGeometry args={[0.012, 0.03, 0.7 * it.s, 4]} />
              <meshStandardMaterial color="#241e14" roughness={1} />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

// ── FLOOR MIST (3D-ENV-1) — a cold layer creeping over the
// flagstones. Two translucent discs, one useFrame, refs only.
function FloorMist({ bounds, tint = "#6a8a9a" }) {
  const grp = useRef();
  const data = useMemo(() => {
    const R = mulberry32(923);
    const w = (bounds.maxX - bounds.minX) * UNIT, d = (bounds.maxY - bounds.minY) * UNIT;
    return Array.from({ length: 2 }, (_, i) => ({
      x: (R() - 0.5) * w * 0.5, z: (R() - 0.5) * d * 0.5,
      r: Math.min(w, d) * (0.32 + R() * 0.14), spd: 0.08 + R() * 0.07, ph: R() * 6, k: i,
    }));
  }, [bounds]);
  useFrame((s) => {
    if (!grp.current) return;
    const t = s.clock.elapsedTime;
    grp.current.children.forEach((m, i) => {
      const d = data[i];
      m.position.x = d.x + Math.sin(t * d.spd + d.ph) * 1.6;
      m.position.z = d.z + Math.cos(t * d.spd * 0.85 + d.ph) * 1.3;
      m.material.opacity = 0.05 + 0.03 * Math.sin(t * 0.35 + d.ph);
    });
  });
  return (
    <group ref={grp}>
      {data.map((d) => (
        <mesh key={d.k} position={[d.x, 0.35, d.z]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[d.r, 18]} />
          <meshBasicMaterial color={tint} transparent opacity={0.07} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

// ── DUST MOTES (3D-ENV-1) — slow cold specks drifting DOWN (this
// is a tomb, not a meadow). One useFrame, refs only.
function DustMotes({ bounds }) {
  const grp = useRef();
  const data = useMemo(() => {
    const R = mulberry32(457);
    const w = (bounds.maxX - bounds.minX) * UNIT, d = (bounds.maxY - bounds.minY) * UNIT;
    return Array.from({ length: 20 }, (_, i) => ({
      x: (R() - 0.5) * w * 0.9, z: (R() - 0.5) * d * 0.9,
      y: R() * 4, spd: 0.1 + R() * 0.14, ph: R() * 6, k: i,
    }));
  }, [bounds]);
  useFrame((s, dt) => {
    if (!grp.current) return;
    const t = s.clock.elapsedTime;
    grp.current.children.forEach((m, i) => {
      const d = data[i];
      m.position.y -= d.spd * dt;
      if (m.position.y < 0.1) m.position.y = 4.2;
      m.position.x = d.x + Math.sin(t * 0.4 + d.ph) * 0.3;
      m.material.opacity = 0.1 + 0.2 * Math.abs(Math.sin(t * 0.6 + d.ph));
    });
  });
  return (
    <group ref={grp}>
      {data.map((d) => (
        <mesh key={d.k} position={[d.x, d.y, d.z]}>
          <sphereGeometry args={[0.03, 4, 4]} />
          <meshBasicMaterial color="#9ab0c0" transparent opacity={0.2} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

// ── pillars — REAL geometry. This is the crypt's whole 3D payoff:
// they physically stand between you and her, and the Roar counter
// (line-of-sight break) is now something you can see.
// ENV-1: painted fluted stone with a cold-glowing carved rune band
// (map = emissiveMap trick — only the runes burn). Rubble form in
// phase 2 keeps its Ship-1 shape, now in matching painted stone.
function Pillars({ pillars, world, rubble = false }) {
  const tex = useMemo(pillarTex, []);
  return (
    <group>
      {pillars.map((p, i) => {
        const [x, , z] = world(p.x, p.y);
        if (rubble) {
          // Phase 2: pillars are broken rubble (no more cover)
          return (
            <group key={i} position={[x, 0, z]}>
              <mesh position={[0, 0.3, 0]} castShadow><boxGeometry args={[1.0, 0.6, 1.0]} /><meshStandardMaterial map={tex} roughness={1} /></mesh>
              <mesh position={[0.3, 0.7, -0.2]} rotation={[0.3, 0.5, 0.2]} castShadow><boxGeometry args={[0.5, 0.5, 0.5]} /><meshStandardMaterial color="#2a2c30" roughness={1} /></mesh>
              <mesh position={[-0.4, 0.15, 0.3]} rotation={[0.1, 0.9, 0.3]}><boxGeometry args={[0.4, 0.3, 0.35]} /><meshStandardMaterial color="#26282c" roughness={1} /></mesh>
              <mesh position={[0.5, 0.1, 0.4]}><dodecahedronGeometry args={[0.18, 0]} /><meshStandardMaterial color="#2c2e33" roughness={1} /></mesh>
            </group>
          );
        }
        return (
          <group key={i} position={[x, 0, z]}>
            <mesh position={[0, 1.9, 0]} castShadow receiveShadow>
              <cylinderGeometry args={[0.55, 0.65, 3.8, 10]} />
              <meshStandardMaterial map={tex} emissiveMap={tex} emissive="#7fd8e8" emissiveIntensity={0.5} roughness={1} />
            </mesh>
            {/* base + capital */}
            <mesh position={[0, 0.15, 0]} castShadow><boxGeometry args={[1.3, 0.3, 1.3]} /><meshStandardMaterial color="#2a2c30" roughness={1} /></mesh>
            <mesh position={[0, 3.85, 0]} castShadow><boxGeometry args={[1.4, 0.4, 1.4]} /><meshStandardMaterial color="#2a2c30" roughness={1} /></mesh>
            {/* capital cap trim */}
            <mesh position={[0, 4.1, 0]}><boxGeometry args={[1.2, 0.12, 1.2]} /><meshStandardMaterial color="#1c1e22" /></mesh>
          </group>
        );
      })}
    </group>
  );
}

// ── enemy actor (prowlers + boss). Phased → translucent glow.
function FoldEnemy({ enemy, world, isBoss = false }) {
  const grp = useRef();
  const body = useRef();
  const cur = useRef(new THREE.Vector3(...world(enemy.pos.x, enemy.pos.y)));
  const target = useRef(new THREE.Vector3(...world(enemy.pos.x, enemy.pos.y)));
  const facing = useRef({ x: 0, y: 1 });
  useEffect(() => {
    const [tx, , tz] = world(enemy.pos.x, enemy.pos.y);
    const dx = tx - cur.current.x, dz = tz - cur.current.z;
    if (Math.abs(dx) > 0.001 || Math.abs(dz) > 0.001) facing.current = { x: dx, y: dz };
    target.current.set(tx, 0, tz);
  }, [enemy.pos.x, enemy.pos.y, world]);
  const phased = enemy.phased || enemy.state === "phased";
  const telegraph = enemy.state === "telegraph" || enemy.telegraphing;
  useFrame((s) => {
    cur.current.lerp(target.current, isBoss ? 0.12 : 0.2);
    if (grp.current) grp.current.position.copy(cur.current);
    // wind-up tell: rear up + bob during telegraph (bigger for HER)
    if (body.current) {
      const amp = isBoss ? 0.14 : 0.08;
      body.current.position.y = telegraph ? Math.abs(Math.sin(s.clock.elapsedTime * 10)) * amp : 0;
    }
  });
  if (enemy.alive === false) return null;
  return (
    <group ref={grp}>
      <group ref={body} scale={isBoss ? [1.6, 1.6, 1.6] : [1, 1, 1]}>
        <EnemyMesh family={enemy.family || "spectral"} role={enemy.role || (isBoss ? "subboss" : "mook")} id={enemy.id} phased={phased} telegraph={telegraph} facing={facing.current} />
      </group>
      {!phased && <GroundBlob r={isBoss ? 0.9 : 0.4} opacity={0.32} />}
      {isBoss && enemy.anima != null && <BossBar enemy={enemy} />}
      {phased && <Label position={[0, isBoss ? 3.2 : 2.0, 0]} text="phased" accent="#9fe8ff" small z={[24, 16]} />}
    </group>
  );
}

function BossBar({ enemy }) {
  const frac = Math.max(0, Math.min(1, enemy.anima / (enemy.animaMax || 8000)));
  return (
    <group position={[0, 3.4, 0]}>
      <mesh><boxGeometry args={[2.4, 0.16, 0.03]} /><meshBasicMaterial color="#0c0a08" /></mesh>
      <mesh position={[-(2.4 * (1 - frac)) / 2, 0, 0.02]}><boxGeometry args={[2.4 * frac, 0.12, 0.03]} /><meshBasicMaterial color="#c9426a" toneMapped={false} /></mesh>
    </group>
  );
}

// ── the player ───────────────────────────────────────────────
function FoldPlayer({ character, tier, ctrlPos, ctrlFacing, dim, swing, dodging, followRef = null, aimRef = null, world }) {
  const grp = useRef();
  const cur = useRef(new THREE.Vector3(...world(ctrlPos.x, ctrlPos.y)));
  const target = useRef(new THREE.Vector3(...world(ctrlPos.x, ctrlPos.y)));
  const moving = useRef(false);
  useEffect(() => { const [tx, , tz] = world(ctrlPos.x, ctrlPos.y); target.current.set(tx, 0, tz); }, [ctrlPos.x, ctrlPos.y, world]);
  useFrame(() => {
    const before = cur.current.clone();
    cur.current.lerp(target.current, 0.25);
    moving.current = cur.current.distanceTo(before) > 0.004;
    if (grp.current) grp.current.position.copy(cur.current);
    if (followRef) followRef.current = cur.current;
    if (aimRef) aimRef.current = cur.current;
  });
  return (
    <group ref={grp}>
      <PlayerBody beingId={character?.beingId || null} tier={tier} facing={ctrlFacing} moving={moving.current} dim={dim} swing={swing} dodging={dodging} />
    </group>
  );
}

// ── torches for atmosphere (crypt corners) — ENV-1: iron sconce
// bracket, layered cold flame (outer + white core), same light.
function Torch({ position }) {
  const flame = useRef();
  const core = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (flame.current) { flame.current.scale.y = 1 + Math.sin(t * 7) * 0.2; flame.current.material.emissiveIntensity = 1.4 + Math.sin(t * 9) * 0.4; }
    if (core.current) { core.current.scale.y = 1 + Math.sin(t * 8 + 1) * 0.25; core.current.material.opacity = 0.7 + Math.sin(t * 11) * 0.2; }
  });
  return (
    <group position={position}>
      {/* iron stand + bracket ring */}
      <mesh position={[0, 0.06, 0]}><cylinderGeometry args={[0.14, 0.18, 0.12, 7]} /><meshStandardMaterial color="#1c1a18" roughness={1} /></mesh>
      <mesh position={[0, 1, 0]}><cylinderGeometry args={[0.05, 0.05, 2, 6]} /><meshStandardMaterial color="#2a2018" /></mesh>
      <mesh position={[0, 1.9, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.12, 0.025, 5, 10]} /><meshStandardMaterial color="#1c1a18" roughness={1} /></mesh>
      {/* the bowl */}
      <mesh position={[0, 1.96, 0]}><cylinderGeometry args={[0.14, 0.08, 0.14, 7]} /><meshStandardMaterial color="#2a2620" roughness={1} /></mesh>
      {/* cold flame — outer + white-blue core */}
      <mesh ref={flame} position={[0, 2.18, 0]}><coneGeometry args={[0.18, 0.5, 6]} /><meshStandardMaterial color="#7fc8ff" emissive="#4a9fff" emissiveIntensity={1.5} transparent opacity={0.9} toneMapped={false} /></mesh>
      <mesh ref={core} position={[0, 2.1, 0]}><coneGeometry args={[0.09, 0.28, 5]} /><meshBasicMaterial color="#e0f4ff" transparent opacity={0.8} toneMapped={false} depthWrite={false} /></mesh>
      <pointLight position={[0, 2.1, 0]} color="#6ab0ff" intensity={2.5} distance={9} />
    </group>
  );
}

// ── DESCENT scene — corridor gauntlet, follow camera ─────────
function DescentScene({ character, tier, ctrlPos, ctrlFacing, dim, swing, dodging, enemies, floats, geo }) {
  const world = useMemo(() => makeWorld(geo.bounds), [geo.bounds]);
  const followRef = useRef(new THREE.Vector3(...world(ctrlPos.x, ctrlPos.y)));
  const [dx, , dz] = world(geo.door.x, geo.door.y);
  return (
    <>
      <color attach="background" args={["#080a0c"]} />
      <fog attach="fog" args={["#0e1216", 12, 42]} />
      <ambientLight intensity={0.32} />
      <hemisphereLight args={["#1a2430", "#060606", 0.4]} />
      <directionalLight position={[10, 26, 8]} intensity={0.55} castShadow shadow-mapSize-width={1024} shadow-mapSize-height={1024} shadow-camera-far={80} />
      <directionalLight position={[-16, 12, -12]} intensity={0.3} color="#7c9fff" />

      <CryptFloor bounds={geo.bounds} tint="#1a1e22" />
      <Walls bounds={geo.bounds} />
      <CryptDecor bounds={geo.bounds} roots />
      <FloorMist bounds={geo.bounds} />
      <DustMotes bounds={geo.bounds} />
      <Torch position={[dx - 1.5, 0, dz - 2]} />
      <Torch position={[dx - 1.5, 0, dz + 2]} />

      {/* the sealed door out (unseals when prowlers cleared — the
          grove/fold decides; we just color it) — ENV-1: a stone
          frame around the slab, plank grooves, glowing seam when open */}
      <group position={[dx, 0, dz]}>
        <mesh position={[-1.05, 1.6, 0]}><boxGeometry args={[0.3, 3.2, 0.5]} /><meshStandardMaterial color="#1c1e22" roughness={1} /></mesh>
        <mesh position={[1.05, 1.6, 0]}><boxGeometry args={[0.3, 3.2, 0.5]} /><meshStandardMaterial color="#1c1e22" roughness={1} /></mesh>
        <mesh position={[0, 3.3, 0]}><boxGeometry args={[2.4, 0.3, 0.5]} /><meshStandardMaterial color="#1c1e22" roughness={1} /></mesh>
        <mesh position={[0, 1.6, 0]}><boxGeometry args={[1.8, 3.2, 0.4]} /><meshStandardMaterial color={geo.doorOpen ? "#3a5a4a" : "#26282c"} emissive={geo.doorOpen ? "#4ae89f" : "#000"} emissiveIntensity={geo.doorOpen ? 0.4 : 0} roughness={1} /></mesh>
        {geo.doorOpen && (
          <mesh position={[0, 1.6, 0.22]}>
            <planeGeometry args={[0.12, 3.0]} />
            <meshBasicMaterial color="#4ae89f" transparent opacity={0.6} toneMapped={false} depthWrite={false} />
          </mesh>
        )}
        <Label position={[0, 3.6, 0]} text={geo.doorOpen ? "The Crypt ↓ (open)" : "Sealed — clear the Descent"} accent={geo.doorOpen ? "#4ae89f" : "#8a8a8a"} small />
      </group>

      {enemies.map((e) => <FoldEnemy key={e.id} enemy={e} world={world} />)}
      {floats.map((f) => { const [x, , z] = world(f.x, f.y); return <Floaty key={f.id} position={[x, 1.6, z]} text={f.text} color={f.color} />; })}

      <FoldPlayer character={character} tier={tier} ctrlPos={ctrlPos} ctrlFacing={ctrlFacing} dim={dim} swing={swing} dodging={dodging} followRef={followRef} world={world} />
      <FollowRig followRef={followRef} trail={[0.001, 11, 13]} look={[0, 1.0, 0]} min={5} max={40} />
    </>
  );
}

// ── CRYPT scene — arena, STAGE camera (fixed mark, re-aims only) ─
function CryptScene({ character, tier, ctrlPos, ctrlFacing, dim, swing, dodging, enemies, boss, cubs, floats, geo, phase2 }) {
  const world = useMemo(() => makeWorld(geo.bounds), [geo.bounds]);
  const aimRef = useRef(new THREE.Vector3(...world(ctrlPos.x, ctrlPos.y)));
  // Solve the stage mark ONCE from the room size — a high wide
  // cinematic shot looking down the long axis. No per-frame tuning.
  const mark = useMemo(() => {
    const d = (geo.bounds.maxY - geo.bounds.minY + 2) * UNIT;
    return [0, d * 0.9, d * 0.85];
  }, [geo.bounds]);

  return (
    <>
      <color attach="background" args={["#070809"]} />
      <fog attach="fog" args={["#0a0d10", 14, 46]} />
      <ambientLight intensity={phase2 ? 0.22 : 0.34} />
      <hemisphereLight args={["#14202c", "#050505", 0.4]} />
      <directionalLight position={[6, 30, 10]} intensity={phase2 ? 0.4 : 0.6} castShadow shadow-mapSize-width={2048} shadow-mapSize-height={2048} shadow-camera-left={-24} shadow-camera-right={24} shadow-camera-top={24} shadow-camera-bottom={-24} shadow-camera-far={90} />
      <directionalLight position={[-18, 14, -14]} intensity={0.3} color="#a04aff" />

      <CryptFloor bounds={geo.bounds} tint={phase2 ? "#141014" : "#1a1620"} />
      <Walls bounds={geo.bounds} />
      <CryptDecor bounds={geo.bounds} />
      <FloorMist bounds={geo.bounds} tint={phase2 ? "#8a6a9a" : "#6a8a9a"} />
      <DustMotes bounds={geo.bounds} />
      <Pillars pillars={geo.pillars} world={world} rubble={phase2} />

      {/* corner torches — go colder/dimmer in phase 2 */}
      {[[geo.bounds.minX, geo.bounds.minY], [geo.bounds.maxX, geo.bounds.minY], [geo.bounds.minX, geo.bounds.maxY], [geo.bounds.maxX, geo.bounds.maxY]].map(([gx, gy], i) => {
        const [x, , z] = world(gx, gy);
        return <Torch key={i} position={[x, 0, z]} />;
      })}

      {/* cubs (adds that walk home to heal her) */}
      {cubs && cubs.map((c) => c.alive !== false && (
        <group key={c.id} position={world(c.pos.x, c.pos.y)}>
          <EnemyMesh family="spectral" role="mook" id={c.id} phased={false} facing={{ x: 0, y: 1 }} />
          <GroundBlob r={0.3} opacity={0.25} />
        </group>
      ))}

      {/* remaining descent-style adds if any */}
      {enemies && enemies.map((e) => <FoldEnemy key={e.id} enemy={e} world={world} />)}

      {/* HER */}
      {boss && <FoldEnemy enemy={boss} world={world} isBoss />}

      {floats.map((f) => { const [x, , z] = world(f.x, f.y); return <Floaty key={f.id} position={[x, 1.6, z]} text={f.text} color={f.color} />; })}

      <FoldPlayer character={character} tier={tier} ctrlPos={ctrlPos} ctrlFacing={ctrlFacing} dim={dim} swing={swing} dodging={dodging} aimRef={aimRef} world={world} />
      <StageRig mark={mark} aimRef={aimRef} aimHeight={1.4} />
    </>
  );
}

// ── EXPORT — `stage` picks the chamber. All live state passed in.
export default function SunkenFold3D({
  stage = "descent",     // "descent" | "crypt"
  character = null,
  ctrlPos = { x: 2, y: 5 },
  ctrlFacing = { x: 1, y: 0 },
  dim = false,
  swing = null,          // ctrl.lastSwing — fires the 3D strike
  dodging = false,       // ctrl.dodging — bends into a roll
  keysRef = null,        // ctrl.keysRef — TouchControls writes into it
  onAttack = null,       // bind to ctrl.attack (mobile ⚔)
  onDodge = null,        // bind to ctrl.dodge  (mobile ↻)
  enemies = [],
  boss = null,
  cubs = [],
  floats = [],
  geo,                   // chamber geometry: { bounds, door?, doorOpen?, pillars? }
  phase2 = false,
  prompt = null,
  feltEvent = null,
  downed = false,
  onBack = () => {},
}) {
  const tier = useMemo(() => {
    if (!character) return null;
    const { level } = levelForXp(character.xp || 0);
    return tierForLevel(level);
  }, [character]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "#070809" }}>
      <Canvas shadows camera={{ position: [0, 14, 16], fov: 50 }} dpr={[1, 2]}>
        {stage === "descent" ? (
          <DescentScene character={character} tier={tier} ctrlPos={ctrlPos} ctrlFacing={ctrlFacing} dim={dim} swing={swing} dodging={dodging} enemies={enemies} floats={floats} geo={geo} />
        ) : (
          <CryptScene character={character} tier={tier} ctrlPos={ctrlPos} ctrlFacing={ctrlFacing} dim={dim} swing={swing} dodging={dodging} enemies={enemies} boss={boss} cubs={cubs} floats={floats} geo={geo} phase2={phase2} />
        )}
      </Canvas>

      <TouchControls keysRef={keysRef} onAttack={onAttack} onDodge={onDodge} accent="#c9426a" />

      <button style={backBtn} onClick={onBack}>← Leave the Fold</button>
      {prompt ? <div style={promptBox}>{prompt}</div> : null}
      {feltEvent ? <div style={feltBox}>{feltEvent}</div> : null}
      {downed ? (
        <div style={downVeil}>
          <div style={downText}>the dark takes you</div>
          <div style={downSub}>you wake at the Old Fold — nothing is lost, nothing is rested</div>
        </div>
      ) : null}
      <div style={hintBox}>WASD / arrows to move · J or Space to strike · K or Shift to dodge · pillars break her Roar</div>
    </div>
  );
}

// ── chrome (matches the fold's language) ─────────────────────
const backBtn = {
  position: "absolute", top: 14, left: 14, zIndex: 20,
  padding: "8px 14px", background: "rgba(20,16,25,0.9)", color: "#e9ddc4",
  border: "2px solid #c9426a", borderRadius: 8, fontFamily: "Georgia, serif",
  fontWeight: 700, fontSize: 13, cursor: "pointer", boxShadow: "0 3px 0 #141019",
};
const promptBox = {
  position: "absolute", bottom: 58, left: "50%", transform: "translateX(-50%)",
  zIndex: 20, background: "rgba(20,16,25,0.92)", color: "#f0d060",
  border: "1px solid #c9a84c", borderRadius: 10, padding: "8px 18px",
  fontFamily: "Georgia, serif", fontSize: 14, fontWeight: 700, textShadow: "0 1px 2px #000",
};
const feltBox = {
  position: "absolute", top: 70, left: "50%", transform: "translateX(-50%)",
  zIndex: 20, background: "rgba(20,16,25,0.85)", color: "#c9b8ff",
  border: "1px solid #a04aff", borderRadius: 10, padding: "7px 16px",
  fontFamily: "Georgia, serif", fontSize: 13, letterSpacing: 0.5, textShadow: "0 1px 2px #000",
};
const hintBox = {
  position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
  zIndex: 20, background: "rgba(20,16,25,0.82)", color: "#e9ddc4",
  border: "1px solid #c9426a", borderRadius: 10, padding: "7px 16px",
  fontFamily: "Georgia, serif", fontSize: 12.5, letterSpacing: 0.4, textShadow: "0 1px 2px #000",
};
const downVeil = {
  position: "absolute", inset: 0, zIndex: 40, display: "flex", flexDirection: "column",
  alignItems: "center", justifyContent: "center", gap: 10,
  background: "rgba(4,3,5,0.85)", backdropFilter: "blur(3px)",
};
const downText = { color: "#d8c0ff", fontFamily: "Georgia, serif", fontSize: 30, fontWeight: 800, textShadow: "0 2px 8px #000" };
const downSub = { color: "#a890c0", fontFamily: "Georgia, serif", fontSize: 15, textShadow: "0 1px 3px #000" };
